import { ShelfLifeBucket } from '../types/foodRecognition';
import { SHELF_LIFE_PROMPT_BLOCK, SHELF_LIFE_PROMPT_EXAMPLES } from './shelfLifePrompt';

/**
 * Pure parsing helpers for a Lufa Farms order email.
 *
 * These live apart from `lufaImportService` so both sides can use them: the
 * client, which posts the pasted email body to /api/ai/generate, and the
 * inbound-email handler, which calls Gemini directly. Nothing in here touches
 * the network, Redux or React.
 */

export interface ParsedDeliveryItem {
  name: string;       // English name
  nameFr: string;     // French name (cleaned)
  /**
   * 'protein' is the legacy value and is still accepted: it lumped a whole
   * chicken in with a block of tofu, filed both as Plant-Based Protein, and
   * gave both fourteen days. New responses split it.
   */
  category: 'produce' | 'meat' | 'plant-protein' | 'protein' | 'dairy' | 'bakery' | 'frozen' | 'other';
  quantity: number;
  unit: string;
  isFrozen: boolean;
  /** How long it keeps, in the shared vocabulary. See shelfLifePrompt. */
  shelfLife?: ShelfLifeBucket;
  /** Delivered sealed unless the line says otherwise; here for symmetry with the photo scan. */
  opened?: boolean;
}

export interface ParsedDeliveryResult {
  items: ParsedDeliveryItem[];
  deliveryDate: string | null;  // ISO date string YYYY-MM-DD
  error?: string;
}

const FRENCH_MONTHS: Record<string, string> = {
  'janvier': '01', 'février': '02', 'mars': '03', 'avril': '04',
  'mai': '05', 'juin': '06', 'juillet': '07', 'août': '08',
  'septembre': '09', 'octobre': '10', 'novembre': '11', 'décembre': '12',
};

/**
 * Does this email look like a Lufa order confirmation?
 *
 * Two signals are required. One is not enough: a recipe newsletter linking to
 * lufa.com would be diverted into the pantry importer, and an order-shaped
 * email from another grocer would be parsed with Lufa's prompt.
 */
export function isLufaOrderEmail(html: string, text: string): boolean {
  const body = `${html || ''}\n${text || ''}`.toLowerCase();
  const fromLufa = body.includes('lufa.com') || body.includes('fermes lufa');
  if (!fromLufa) return false;

  return (
    /num[ée]ro de commande/.test(body) ||
    /order number/.test(body) ||
    /liste des produits de votre panier/.test(body) ||
    /your basket'?s? product list/.test(body)
  );
}

/**
 * The order number, which is stable across forwards of the same delivery and so
 * is what import idempotency keys on. The label and the value sit on separate
 * lines in the real email.
 */
export function extractOrderNumber(text: string): string | null {
  const match = text.match(/(?:num[ée]ro de commande|order number)\s*:?\s*(\d{4,})/i);
  return match ? match[1] : null;
}

export function extractDeliveryDate(emailText: string): string | null {
  // Match patterns like "02 mars 2026" or "2 mars 2026"
  const dateRegex = /(\d{1,2})\s+(janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre)\s+(\d{4})/i;
  const match = emailText.match(dateRegex);
  if (!match) return null;

  const day = match[1].padStart(2, '0');
  const month = FRENCH_MONTHS[match[2].toLowerCase()];
  const year = match[3];
  if (!month) return null;

  return `${year}-${month}-${day}`;
}

export function cleanGeminiResponse(text: string): string {
  let cleaned = text.trim();
  if (cleaned.startsWith('```json')) {
    cleaned = cleaned.replace(/^```json\s*/, '').replace(/\s*```$/, '');
  } else if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```\s*/, '').replace(/\s*```$/, '');
  }
  return cleaned;
}

/**
 * The prompt asks for an English unit, and the model regularly answers with the
 * one printed on the French order line instead — which is how a pantry row ends
 * up reading "1 sac".
 */
const UNIT_TRANSLATIONS: Record<string, string> = {
  'sac': 'bag',
  'sachet': 'bag',
  'casseau': 'punnet',
  'botte': 'bunch',
  'barquette': 'punnet',
  'pot': 'jar',
  'boîte': 'box',
  'boite': 'box',
  'paquet': 'pack',
  'tête': 'head',
  'tranche': 'slice',
  'douzaine': 'dozen',
  'litre': 'L',
};

export function normalizeDeliveryUnit(unit: string | undefined): string | undefined {
  if (!unit) return unit;
  return UNIT_TRANSLATIONS[unit.trim().toLowerCase()] ?? unit;
}

/** "Cadeau : Sucre de canne biologique" -> "Sucre de canne biologique". */
export function stripGiftPrefix(name: string): string {
  return name.replace(/^\s*(cadeau|gift|free)\s*:\s*/i, '').trim();
}

/**
 * "Bacon maison (congelé)" -> "Bacon maison".
 *
 * Only the temperature state, which the pantry already says twice over: the row
 * sits in the Freezer zone and carries a freshness pill. A parenthetical that
 * describes the food itself — "(tranché)", "(sliced)" — stays, because it
 * changes what you actually have.
 */
export function stripTemperatureSuffix(name: string): string {
  return name
    .replace(/\s*\((frais|fra[îi]che|fresh|congel[ée]e?s?|surgel[ée]e?s?|frozen)\)\s*$/i, '')
    .trim();
}

/**
 * Charges and totals. Anchored on whole words and on "frais de", never a bare
 * "frais": the order lists "Poulet entier (frais)", and matching that as a fee
 * silently dropped the chicken.
 */
const CHARGE_PATTERNS: RegExp[] = [
  /\b(livraison|delivery|shipping)\b/,
  /\bfrais de\b/,
  /\b(tps|tvq|gst|qst|taxes?)\b/,
  /\b(sous-total|subtotal|total)\b/,
  /\bconsigne\b/,
  /\bcr[ée]dit/,
];

/** Real products that are not food and have no business in a pantry. */
const NON_EDIBLE_PATTERNS: RegExp[] = [
  /\btampons?\b/,
  /\bserviettes? (hygi[ée]nique|sanitaire)/,
  /\bcoupe menstruelle\b/,
  /\bsavons?\b/,
  /\bshampoo?ing\b/,
  /\bd[ée]odorant\b/,
  /\bdentifrice\b/,
  /\btoothpaste\b/,
  /\bpapier (hygi[ée]nique|de toilette)\b/,
  /\btoilet paper\b/,
  /\bessuie-tout\b/,
  /\bpaper towels?\b/,
  /\bd[ée]tergent\b/,
  /\bd[ée]tachant\b/,
  /\blessive\b/,
  /\blaundry\b/,
  /\bliti[èe]re\b/,
  /\bcat litter\b/,
  /\bchandelles?\b/,
  /\bcandles?\b/,
];

/**
 * Split the model's output into what belongs in the pantry and what does not.
 *
 * The skipped half is not discarded quietly — the confirmation email lists it,
 * so a food filtered out by mistake is visible without opening the app.
 */
export function partitionFoodItems(
  items: ParsedDeliveryItem[]
): { food: ParsedDeliveryItem[]; skipped: ParsedDeliveryItem[] } {
  const food: ParsedDeliveryItem[] = [];
  const skipped: ParsedDeliveryItem[] = [];

  for (const raw of items) {
    const cleaned: ParsedDeliveryItem = {
      ...raw,
      name: stripTemperatureSuffix(stripGiftPrefix(raw.name || '')),
      nameFr: stripTemperatureSuffix(stripGiftPrefix(raw.nameFr || '')),
      unit: normalizeDeliveryUnit(raw.unit) ?? raw.unit,
    };
    const combined = `${cleaned.name} ${cleaned.nameFr}`.toLowerCase();

    const isCharge = CHARGE_PATTERNS.some(p => p.test(combined));
    const isNonEdible = NON_EDIBLE_PATTERNS.some(p => p.test(combined));

    if (isCharge || isNonEdible) {
      skipped.push(cleaned);
    } else {
      food.push(cleaned);
    }
  }

  return { food, skipped };
}

export const LUFA_PARSE_PROMPT = `You are parsing a Lufa Farms delivery order email. Extract ONLY food items from the product list.

RULES:
- Skip non-food lines entirely: delivery fees ("Livraison"), taxes (TPS, TVQ), bottle deposits ("Consigne"), subtotals, totals, credits
- Skip products that are not edible: hygiene products (tampons, soap, toothpaste), paper goods, cleaning products, pet supplies
- Clean product names: remove cultivar/variety names (e.g., "Darkibor", "Azirka", "Green Banner", "Rexoso"), harvest years (e.g., "récolte 2025"), size ranges (e.g., "5 à 9")
- Remove a leading "Cadeau :" or "Gift:" from the name, but KEEP the item — a free bag of sugar is still sugar
- Keep the core food name in French (nameFr) and provide an English translation (name)
- Detect frozen items from keywords like "surgelé", "congelé"
- Categorize: produce, meat (chicken, beef, pork, fish, seafood, bacon, ham, sausage), plant-protein (tofu, tempeh, legumes, nuts), dairy, bakery, frozen, other
- "meat" and "plant-protein" are different categories. A whole chicken is meat; a block of tofu is plant-protein.

${SHELF_LIFE_PROMPT_BLOCK}

${SHELF_LIFE_PROMPT_EXAMPLES}

QUANTITY RULES (important):
- When the product name contains a count range like "(5 à 9)", use the LOWEST number as quantity (e.g., quantity: 5, unit: "unit")
- When the product name contains a weight like "(2 lb)" or "(500g)", use that weight (e.g., quantity: 2, unit: "lb")
- When the product name contains a volume like "(1L)", use that volume (e.g., quantity: 1, unit: "L")
- For items sold "à l'unité" with no size info, use the email's "Quantité" value
- For items sold "/ portion" or "/ sac", use the email's "Quantité" value with unit "portion" or "bag"

Return a JSON array of objects with this exact structure:
[
  {
    "name": "English name",
    "nameFr": "Clean French name",
    "category": "produce|meat|plant-protein|dairy|bakery|frozen|other",
    "quantity": 5,
    "unit": "unit|portion|bag|head|lb|g|L",
    "isFrozen": false,
    "shelfLife": "highly-perishable|perishable|week-plus|keeps-weeks|shelf-stable"
  }
]

Return ONLY the JSON array, no other text.

EMAIL TEXT:
`;
